import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Container,
  Box,
  Card,
  CardContent,
  InputAdornment,
  Typography,
} from '@mui/material';
import Email from '@mui/icons-material/Email';
import Lock from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Person from '@mui/icons-material/Person';

import { ASSETS } from '../../../const/assets';
import {
  useRegister,
  useSendVerificationEmail,
} from '../../../hooks/useAuthencation';
import { useNavigate } from 'react-router-dom';

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .max(32, 'Password must not exceed 32 characters')
    .matches(/[A-Z]/, 'At least one uppercase letter required')
    .matches(/[a-z]/, 'At least one lowercase letter required')
    .matches(/[0-9]/, 'At least one number required')
    .matches(/[\W_]/, 'At least one special character required')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
});

const otpValidationSchema = Yup.object().shape({
  otpCode: Yup.string()
    .required('OTP code is required')
    .matches(/^\d+$/, 'OTP code must be a number'),
});

interface RegisterFormValues {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

interface VerifyEmailFormValues {
  otpCode: string;
}

function Register() {
  const navigate = useNavigate();
  const { mutateAsync: register, isPending } = useRegister();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [info, setInfo] = useState<{
    email: string;
    firstName: string;
    lastName: string;
    password: string;
  }>({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
  });
  const [isStepVerifyEmail, setIsStepVerifyEmail] = useState(false);

  const {
    mutateAsync: sendVerificationEmail,
    isPending: isSendingVerificationEmail,
  } = useSendVerificationEmail();

  const handleSendVerificationEmail = async (values: RegisterFormValues) => {
    try {
      setInfo({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
      });
      await sendVerificationEmail({
        email: values.email,
        firstName: values.firstName,
      });
      setIsStepVerifyEmail(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (values: VerifyEmailFormValues) => {
    try {
      const payload = {
        ...info,
        otpCode: values.otpCode,
      };
      await register(payload);

      setTimeout(() => {
        navigate(`/login?email=${info.email}`);
      }, 1000);
    } catch (error) {
      console.log(error);
      return;
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{
        background: '#272727',
      }}
      position="relative"
    >
      <img
        className="absolute top-0 left-0 w-full"
        src={ASSETS.bgLogin}
        alt="bgLogin"
      />
      <Container maxWidth="sm">
        <Card
          sx={{
            padding: '20px',
            borderRadius: '16px',
            boxShadow: 3,
            position: 'relative',
            zIndex: 10,
          }}
        >
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <div
                className="w-10 h-10 flex justify-center items-center border-[#D8DADC] rounded-[10px] border cursor-pointer"
                onClick={() => navigate(-1)}
              >
                <img
                  src={ASSETS.icBack2}
                  alt="back"
                  width={9}
                  height={15}
                  className="cursor-pointer"
                />
              </div>
              <img src={ASSETS.icLogo} alt="logo" width={50} height={50} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                align="left"
                sx={{
                  fontSize: '30px',
                  fontWeight: 'semibold',
                  my: '32px',
                  color: 'black',
                }}
              >
                Sign up with Email
              </Typography>
            </Box>
            {!isStepVerifyEmail ? (
              <Formik
                initialValues={{
                  email: '',
                  firstName: '',
                  lastName: '',
                  password: '',
                  confirmPassword: '',
                }}
                validationSchema={validationSchema}
                onSubmit={handleSendVerificationEmail}
                enableReinitialize={false}
              >
                {({ handleChange, handleBlur, errors, touched, values }) => (
                  <Form>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(12, 1fr)',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ gridColumn: 'span 12' }}>
                        <Field
                          name="email"
                          as={TextField}
                          label="Email"
                          variant="outlined"
                          fullWidth
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={touched.email && errors.email}
                          error={touched.email && Boolean(errors.email)}
                          autoComplete="email"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Email fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                        <Field
                          name="firstName"
                          as={TextField}
                          label="First Name"
                          variant="outlined"
                          fullWidth
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={touched.firstName && errors.firstName}
                          error={touched.firstName && Boolean(errors.firstName)}
                          autoComplete="given-name"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                        <Field
                          name="lastName"
                          as={TextField}
                          label="Last Name"
                          variant="outlined"
                          fullWidth
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={touched.lastName && errors.lastName}
                          error={touched.lastName && Boolean(errors.lastName)}
                          autoComplete="family-name"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box sx={{ gridColumn: 'span 12' }}>
                        <Field
                          name="password"
                          as={TextField}
                          label="Password"
                          type={showPassword ? 'text' : 'password'}
                          variant="outlined"
                          fullWidth
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={touched.password && errors.password}
                          error={touched.password && Boolean(errors.password)}
                          autoComplete="new-password"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <div
                                  onClick={() => setShowPassword(!showPassword)}
                                  className="cursor-pointer"
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </div>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box sx={{ gridColumn: 'span 12' }}>
                        <Field
                          name="confirmPassword"
                          as={TextField}
                          label="Confirm Password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          variant="outlined"
                          fullWidth
                          value={values.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={
                            touched.confirmPassword && errors.confirmPassword
                          }
                          error={
                            touched.confirmPassword &&
                            Boolean(errors.confirmPassword)
                          }
                          autoComplete="new-password"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <div
                                  onClick={() =>
                                    setShowConfirmPassword(!showConfirmPassword)
                                  }
                                  className="cursor-pointer"
                                >
                                  {showConfirmPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </div>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box sx={{ gridColumn: 'span 12' }}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="secondary"
                          fullWidth
                          sx={{
                            borderRadius: '10px',
                            backgroundColor: 'black',
                            color: 'white',
                            height: '44px',
                            fontSize: '16px',
                            fontWeight: 'medium',
                            textTransform: 'none',
                          }}
                          disabled={isSendingVerificationEmail}
                        >
                          {isSendingVerificationEmail
                            ? 'Sending verification email...'
                            : 'Register'}
                        </Button>
                      </Box>
                      <Box sx={{ gridColumn: 'span 12' }}>
                        <Button
                          variant="text"
                          fullWidth
                          onClick={() => navigate('/login')}
                          sx={{
                            color: 'black',
                            fontSize: '14px',
                            textTransform: 'none',
                            fontWeight: 'regular',
                            padding: '0',
                          }}
                        >
                          Already have an account? Login
                        </Button>
                      </Box>
                    </Box>
                  </Form>
                )}
              </Formik>
            ) : (
              <Formik
                initialValues={{
                  otpCode: '',
                }}
                validationSchema={otpValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={false}
              >
                {({ handleChange, handleBlur, errors, touched, values }) => (
                  <Form>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(12, 1fr)',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ gridColumn: 'span 12' }}>
                        <Field
                          name="otpCode"
                          as={TextField}
                          label="OTP Code"
                          variant="outlined"
                          fullWidth
                          value={values.otpCode || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          helperText={touched.otpCode && errors.otpCode}
                          error={touched.otpCode && Boolean(errors.otpCode)}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ gridColumn: 'span 12', mt: '20px' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        fullWidth
                        sx={{
                          borderRadius: '10px',
                          backgroundColor: 'black',
                          color: 'white',
                          height: '44px',
                          fontSize: '16px',
                          fontWeight: 'medium',
                          textTransform: 'none',
                        }}
                        disabled={isPending}
                      >
                        {isPending ? 'Verifying Email...' : 'Verify Email'}
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default Register;
